// Recent sessions list with click-to-expand: each row opens to show the
// session's full per-exercise breakdown (sets, weights, reps, RPE, tags).
// Detail is fetched lazily from /api/stats/session/:id on first expand.

import { useEffect, useState } from 'react';
import { BodyDiagramStatic, type MuscleRow } from './BodyDiagram';
import css from './RecentSessions.module.css';

interface Session {
  id: number;
  date: string;
  source: string;
  mode: string;
  day_id: string | null;
  day_label: string | null;
  active_exercise: string | null;
  notes: string | null;
  started_at: number;
  ended_at: number | null;
  set_count: number;
  exercise_count: number;
}

interface SetRow {
  id: number;
  exercise_id: string;
  exercise_name: string | null;
  set_number: number;
  weight_kg: number | null;
  reps: number | null;
  rpe: number | null;
  warmup: number;
  bfr: number;
  duration_sec: number | null;
  speed_kmh: number | null;
  incline_pct: number | null;
  distance_m: number | null;
  avg_hr_bpm: number | null;
  peak_hr_bpm: number | null;
  elevation_gain_m: number | null;
  calories_kcal: number | null;
  notes: string | null;
}

interface SessionDetail {
  session: {
    id: number;
    mode: string;
    date: string;
    source: string;
    day_id: string | null;
    day_label: string | null;
    started_at: number;
    ended_at: number | null;
    bodyweight_kg: number | null;
    notes: string | null;
  };
  sets: SetRow[];
  tonnage: number;
  muscles: MuscleRow[];
}

const SOURCE_LABEL: Record<string, string> = {
  bilbo: 'bilbo',
  gymbook: 'gymbook',
  setgraph: 'setgraph',
  strava: 'strava',
};

function fmtDuration(startedAt: number, endedAt: number | null): string {
  if (!endedAt) return '—';
  const mins = Math.round((endedAt - startedAt) / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function fmtClock(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function sessionTitle(s: Session): string {
  // Planned sessions: the day's program label is the authoritative title.
  if (s.mode === 'planned' && s.day_label) return s.day_label;
  // Freeform: the user's notes are the title IF they look like a name and
  // not metadata. The active exercise is a decent fallback. Generic last
  // resort labels the session by mode.
  if (s.mode === 'freeform') {
    if (s.active_exercise) return s.active_exercise;
    return 'Freeform session';
  }
  return s.active_exercise || s.notes || '(unnamed session)';
}

function fmtSet(s: SetRow): string {
  const parts: string[] = [];

  if (s.reps != null) {
    if (s.weight_kg != null) parts.push(`${s.reps} × ${s.weight_kg} kg`);
    else parts.push(`${s.reps} reps`);
  }

  if (s.duration_sec != null) {
    const mins = Math.round(s.duration_sec / 60);
    let bit = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`;
    if (s.speed_kmh != null) bit += ` at ${s.speed_kmh} km/h`;
    if (s.incline_pct != null) bit += `, ${s.incline_pct}% incl`;
    if (s.distance_m != null) bit += ` (${(s.distance_m / 1000).toFixed(2)} km)`;
    parts.push(bit);
  } else if (s.distance_m != null) {
    parts.push(`${(s.distance_m / 1000).toFixed(2)} km`);
  }

  if (s.rpe != null) parts.push(`@ RPE ${s.rpe}`);

  const extras: string[] = [];
  if (s.peak_hr_bpm != null) extras.push(`peak HR ${s.peak_hr_bpm}`);
  else if (s.avg_hr_bpm != null) extras.push(`avg HR ${s.avg_hr_bpm}`);
  if (s.elevation_gain_m != null) extras.push(`+${s.elevation_gain_m}m`);
  if (s.calories_kcal != null) extras.push(`${s.calories_kcal} kcal`);
  if (extras.length) parts.push(`(${extras.join(', ')})`);

  return parts.length ? parts.join('  ') : '(no metric)';
}

function SessionDetailView({
  id,
  onDeleted,
}: {
  id: number;
  onDeleted: (id: number) => void;
}) {
  const [data, setData] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/stats/session/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError("couldn't load session detail");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/manage/session/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as
          | { error?: string }
          | null;
        setDeleteError(json?.error ?? 'delete failed');
        setDeleting(false);
        return;
      }
      onDeleted(id);
    } catch {
      setDeleteError('delete failed');
      setDeleting(false);
    }
  }

  if (loading) return <div className={css.detailLoading}>loading session…</div>;
  if (error || !data) return <div className={css.detailError}>{error ?? 'no data'}</div>;

  // Group sets by exercise in order of first appearance.
  const order: string[] = [];
  const byExercise = new Map<string, SetRow[]>();
  for (const s of data.sets) {
    let list = byExercise.get(s.exercise_id);
    if (!list) {
      order.push(s.exercise_id);
      list = [];
      byExercise.set(s.exercise_id, list);
    }
    list.push(s);
  }

  const workingTotal = data.sets.filter((s) => s.warmup === 0).length;

  return (
    <div className={css.detail}>
      {data.muscles && data.muscles.length > 0 && (
        <div className={css.sessionDiagram}>
          <BodyDiagramStatic muscles={data.muscles} compact />
        </div>
      )}
      <div className={css.summary}>
        <span>
          {workingTotal} working set{workingTotal === 1 ? '' : 's'} · {order.length} exercise
          {order.length === 1 ? '' : 's'}
        </span>
        {data.tonnage > 0 && <span>tonnage: {Math.round(data.tonnage).toLocaleString()} kg</span>}
        {data.session.day_label && <span className={css.dim}>{data.session.day_label}</span>}
        <span className={css.dim}>
          started {fmtClock(data.session.started_at)}
          {data.session.ended_at ? ` · ended ${fmtClock(data.session.ended_at)}` : ' · still active'}
        </span>
        {data.session.bodyweight_kg != null && (
          <span className={css.dim}>bw {data.session.bodyweight_kg}kg</span>
        )}
      </div>

      {order.map((exId) => {
        const exSets = byExercise.get(exId) ?? [];
        const name = exSets[0]?.exercise_name ?? exId;
        const working = exSets.filter((s) => s.warmup === 0);
        const tonnage = working
          .filter((s) => s.weight_kg != null && s.reps != null)
          .reduce((acc, s) => acc + (s.weight_kg ?? 0) * (s.reps ?? 0), 0);
        return (
          <div key={exId} className={css.exerciseGroup}>
            <div className={css.exerciseHeader}>
              <span className={css.exerciseName}>{name}</span>
              <span className={css.exerciseStats}>
                {working.length} set{working.length === 1 ? '' : 's'}
                {tonnage > 0 ? ` · ${Math.round(tonnage).toLocaleString()} kg` : ''}
              </span>
            </div>
            <ul className={css.setList}>
              {exSets.map((s, i) => {
                const rowClass = `${css.setRow}${s.warmup ? ' ' + css.warmup : ''}${s.bfr ? ' ' + css.bfr : ''}`;
                return (
                  <li key={s.id} className={rowClass}>
                    <span className={css.setIdx}>#{i + 1}</span>
                    <span>
                      {fmtSet(s)}
                      {s.warmup ? <span className={css.tag}>warmup</span> : null}
                      {s.bfr ? <span className={`${css.tag} ${css.tagBfr}`}>BFR</span> : null}
                      {s.notes ? <span className={css.notes}> — {s.notes}</span> : null}
                    </span>
                    <span />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      <div className={css.deleteRow}>
        {deleteError && <span className={css.detailError}>{deleteError}</span>}
        <button
          type="button"
          className={css.deleteBtn}
          onClick={() => setConfirmOpen(true)}
          disabled={deleting || data.session.ended_at == null}
          title={
            data.session.ended_at == null
              ? 'finish the session in bilbo before deleting'
              : 'delete this session'
          }
        >
          delete session
        </button>
      </div>

      {confirmOpen && (
        <div
          className={css.modalBackdrop}
          onClick={() => !deleting && setConfirmOpen(false)}
        >
          <div
            className={css.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={css.modalTitle}>Delete this session?</div>
            <div className={css.modalBody}>
              {workingTotal} working set{workingTotal === 1 ? '' : 's'}
              {data.tonnage > 0
                ? ` · ${Math.round(data.tonnage).toLocaleString()} kg tonnage`
                : ''}{' '}
              will be removed. This can't be undone.
            </div>
            <div className={css.modalActions}>
              <button
                type="button"
                className={css.modalCancel}
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                cancel
              </button>
              <button
                type="button"
                className={css.modalConfirm}
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting ? 'deleting…' : 'delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function RecentSessions({ data }: { data: Session[] }) {
  const [openId, setOpenId] = useState<number | null>(null);
  // Locally hide sessions the user just deleted. The dashboard's data prop
  // catches up on the next refetch; until then we keep the row out of view.
  const [deletedIds, setDeletedIds] = useState<Set<number>>(() => new Set());

  function onDeleted(id: number) {
    setDeletedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    if (openId === id) setOpenId(null);
  }

  const visible = data.filter((s) => !deletedIds.has(s.id));
  if (visible.length === 0) {
    return <div className={css.empty}>No sessions yet.</div>;
  }
  return (
    <ul className={css.list}>
      {visible.map((s) => {
        const isOpen = openId === s.id;
        return (
          <li key={s.id} className={`${css.item}${isOpen ? ` ${css.itemOpen}` : ''}`}>
            <button
              type="button"
              className={css.header}
              onClick={() => setOpenId(isOpen ? null : s.id)}
              aria-expanded={isOpen}
            >
              <div className={css.date}>{s.date}</div>
              <div className={css.body}>
                <div className={css.title}>{sessionTitle(s)}</div>
                <div className={css.meta}>
                  <span className={css.source}>{SOURCE_LABEL[s.source] ?? s.source}</span>
                  <span>{s.set_count} sets</span>
                  <span>{s.exercise_count} exercises</span>
                  <span>{fmtDuration(s.started_at, s.ended_at)}</span>
                  {s.notes && <span className={css.source}>note: {s.notes}</span>}
                </div>
              </div>
              <div className={css.caret}>{isOpen ? '▴' : '▾'}</div>
            </button>
            {isOpen && <SessionDetailView id={s.id} onDeleted={onDeleted} />}
          </li>
        );
      })}
    </ul>
  );
}
